import { useEffect, useState, type ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  isPartnerServiceUnavailable,
  platformPartnerTypeSlug,
  usePartnersSearch,
  type Partner,
} from '@eis/core';

/**
 * The partner PICKER (SDD-014 R-15): search-as-you-type over the Partner service's live list,
 * each option showing the derived name, the number and the type, and the pick yielding a LINK —
 * the service's code — never a draft and never a name the console would have to match later
 * (O-5). Shared by the lead form and the policy dialogs (insurance) and by the partners module
 * itself, which is why it lives here and not in either module (backbone §1).
 *
 * Two states it has to be honest about:
 *  - `canSearch === false`: the persona may not read the Partner area; the field stays a plain id
 *    input (monospace, as before). The picker decides nothing about permissions — the module
 *    passes the verdict — so no permission literal lives in this package.
 *  - The Partner service down: the search errors with a 5xx, the field says so and keeps taking a
 *    typed code. An outage is an outage, not "no partners".
 *
 * `freeSolo`: a typed code is a valid pick (a partner known from a ticket, a seeded contract id).
 */
export interface PartnerPickerProps {
  /** The picked partner's code (`pr_…`), or the typed id; empty when nothing is picked. */
  readonly value: string;
  readonly onChange: (code: string, partner?: Partner) => void;
  readonly label: string;
  readonly canSearch: boolean;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: ReactNode;
  /** Platform-spelled type slugs the role admits (`person`, `organisation`); options of another type are marked. */
  readonly allowedTypes?: readonly string[];
  readonly 'data-testid'?: string;
}

const DEBOUNCE_MS = 250;

export function PartnerPicker({
  value,
  onChange,
  label,
  canSearch,
  required,
  disabled,
  error,
  helperText,
  allowedTypes,
  'data-testid': testId,
}: PartnerPickerProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [input, setInput] = useState(value);
  const [term, setTerm] = useState('');

  // Debounced: the service is another team's; a request per keystroke would be a load test.
  useEffect(() => {
    const handle = setTimeout(() => {
      setTerm(input.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
    };
  }, [input]);

  const search = usePartnersSearch({ term, pageSize: 10 }, { enabled: canSearch && !disabled && term.length >= 2 });
  const options = search.data?.items ?? [];
  const down = search.isError && isPartnerServiceUnavailable(search.error);

  if (!canSearch) {
    return (
      <TextField
        size="small"
        fullWidth
        label={label}
        value={value}
        required={required}
        disabled={disabled === true}
        error={error}
        helperText={helperText}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false, 'data-testid': testId } }}
      />
    );
  }

  const typeLabel = (partner: Partner): string => {
    const slug = platformPartnerTypeSlug(partner.partnerType.slug);
    return t(`partnerPicker.type.${slug}` as never, { defaultValue: partner.partnerType.name });
  };
  const admitted = (partner: Partner): boolean =>
    allowedTypes === undefined || allowedTypes.length === 0 || allowedTypes.includes(platformPartnerTypeSlug(partner.partnerType.slug));

  return (
    <Autocomplete<Partner, false, false, true>
      freeSolo
      size="small"
      fullWidth
      disabled={disabled === true}
      options={options}
      filterOptions={(candidates) => candidates}
      loading={search.isFetching}
      inputValue={input}
      onInputChange={(_event, next, reason) => {
        setInput(next);
        // A typed code is a pick too (`freeSolo`); a reset is the option's label, not a pick.
        if (reason === 'input') onChange(next);
      }}
      value={options.find((partner) => partner.code === value) ?? (value === '' ? null : value)}
      onChange={(_event, picked) => {
        if (picked === null) {
          onChange('');
          return;
        }
        if (typeof picked === 'string') {
          onChange(picked);
          return;
        }
        onChange(picked.code, picked);
        setInput(picked.code);
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.code)}
      isOptionEqualToValue={(option, selected) =>
        option.code === (typeof selected === 'string' ? selected : selected.code)
      }
      noOptionsText={
        down
          ? t('partnerPicker.unavailable')
          : term.length < 2
            ? t('partnerPicker.typeToSearch')
            : t('partnerPicker.noMatches')
      }
      loadingText={t('partnerPicker.searching')}
      renderOption={(props, partner) => {
        const { key, ...rest } = props as { key: string } & typeof props;
        return (
          <li key={key} {...rest}>
            <Stack spacing={0} sx={{ width: '100%' }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">{partner.displayName === '' ? partner.code : partner.displayName}</Typography>
                <Chip size="small" variant="outlined" label={typeLabel(partner)} color={admitted(partner) ? 'default' : 'warning'} />
                {!admitted(partner) && (
                  <Typography variant="caption" color="warning.main">
                    {t('partnerPicker.typeNotAdmitted')}
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {partner.partnerNumber} · {partner.code}
              </Typography>
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error === true || down}
          helperText={down ? t('partnerPicker.unavailableHint') : (helperText ?? t('partnerPicker.hint'))}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, spellCheck: false, 'data-testid': testId },
          }}
        />
      )}
    />
  );
}
